/**
 * Text-based search field component used within the SearchArea.
 *
 * This component renders a text input field and reports user input changes
 * back to the parent search controller via a standardized callback interface.
 *
 * It acts as a thin view-layer abstraction that translates DOM input events
 * into structured search field updates, including the assigned logical
 * combination operator.
 *
 * The component does not manage state itself. Instead, it delegates all
 * state handling to the parent SearchArea component.
 *
 * @module
 *
 * @author Gregor Schalm gregor.schalm@wum-studios.de
 */

import { IntlMessageValue } from "../../../../../@WUM/core/component/const/IntlMessage";
import React, { ChangeEvent } from "react";
import { searchCombination, TextSearchField } from "./SearchFieldInterface";
import UniInputGroup from "../../../../../@WUM/core/Elements/Form/Elements/InputGroup";
import { UniCol, UniForm } from "../../../../../@WUM/core/Elements";
import UniInputGroupText from "../../../../../@WUM/core/Elements/Form/Elements/InputGroupText";

/**
 * Properties for the SearchText component.
 *
 * Extends the base TextSearchField definition with runtime state
 * and interaction handlers required for controlled search behavior.
 *
 * @typedef {Object} SearchTextProps
 * @property {any} Store - Shared store instance used for internationalization and context.
 * @property {string} value - Current value of the input field.
 * @property {(fieldName: string, value: string, combination: searchCombination) => void} onValueChange - Callback triggered on user input changes.
 */
interface SearchTextProps extends TextSearchField {
    Store: any;
    value: string;
    onValueChange: (fieldName: string, value: string, combination: searchCombination) => void;
}

/**
 * Renders a text input field for search queries.
 *
 * The component listens to input change events and forwards the updated
 * value together with the configured field name and logical combination
 * to the parent handler.
 *
 * @param {SearchTextProps} props - Component configuration and event handlers.
 * @returns {JSX.Element} Rendered text search input field.
 *
 */
export const SearchText = (props: SearchTextProps): JSX.Element => {
    /**
     * Handles input change events and forwards the new value to the parent.
     *
     * The function extracts the value from the native input element and
     * ensures a fallback combination operator if none is defined.
     *
     * @param {ChangeEvent<Element>} event - React change event from the input element.
     * @returns {void} This function does not return a value.
     *
     * @example
     * handleChange(event);
     */
    const handleChange = (event: ChangeEvent<Element>): void => {
        props.onValueChange(
            props.name,
            (event.target as HTMLInputElement).value,
            props.combination ?? "and"
        );
    };

    return (
        <UniCol>
            <UniInputGroup class="mb-3">
                <UniInputGroupText
                    id={props.name}
                    text={IntlMessageValue("de", "inbox.subject", props.Store)}
                />
                <UniForm.FormControl
                    aria-describedby={props.name}
                    name={props.name}
                    onChange={handleChange}
                    defaultValue={props.defaultValue}
                />
            </UniInputGroup>
        </UniCol>
    );
};